import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import InvestorLayout from '../../components/InvestorLayout';
import { User, Mail, Phone, MapPin, CreditCard, Building } from 'lucide-react';

export default function Profile({ user, onLogout }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/investor/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <InvestorLayout user={user} onLogout={onLogout} currentPage="profile">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading profile...</div>
        </div>
      </InvestorLayout>
    );
  }

  return (
    <InvestorLayout user={user} onLogout={onLogout} currentPage="profile">
      <div className="space-y-6" data-testid="profile-page">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            My Profile
          </h1>
          <p className="text-muted-foreground text-lg">View your account information</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="card p-8">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-4xl font-bold">{profile?.full_name?.charAt(0)}</span>
                </div>
                <div>
                  <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {profile?.full_name}
                  </h2>
                  <p className="text-muted-foreground font-mono text-sm mt-1">{profile?.investor_id}</p>
                </div>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  profile?.status === 'active' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                }`}>
                  {profile?.status === 'active' ? 'Active Account' : 'Inactive Account'}
                </span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="card">
              <div className="p-6 border-b">
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Contact Information
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-accent/10 p-3 rounded-lg">
                    <Mail className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email Address</p>
                    <p className="font-medium">{profile?.email}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-accent/10 p-3 rounded-lg">
                    <Phone className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Phone Number</p>
                    <p className="font-medium">{profile?.phone}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-accent/10 p-3 rounded-lg">
                    <MapPin className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="font-medium">{profile?.address}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="p-6 border-b">
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Financial Information
                </h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-success/10 p-3 rounded-lg">
                    <CreditCard className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">PAN Number</p>
                    <p className="font-medium font-mono">{profile?.pan_number || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-success/10 p-3 rounded-lg">
                    <Building className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Bank Account Number</p>
                    <p className="font-medium font-mono">{profile?.bank_account || 'Not provided'}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-success/10 p-3 rounded-lg">
                    <Building className="w-5 h-5 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">IFSC Code</p>
                    <p className="font-medium font-mono">{profile?.ifsc_code || 'Not provided'}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="p-6 border-b">
                <h3 className="text-xl font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Account Details
                </h3>
              </div>
              <div className="p-6 space-y-3">
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-muted-foreground">Member Since</span>
                  <span className="font-medium">
                    {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-muted-foreground">Account Type</span>
                  <span className="font-medium">Individual Investor</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </InvestorLayout>
  );
}